import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, computed, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { debounceTime, fromEvent, map } from 'rxjs';
import { GoogleApiService, UtilityService } from '../../../../services';
import { CommonFieldWidgetBase } from '../common-field-widget-base';
import { AddressData, CommonAddressField, ModifiedAddressComponent } from '../field';
import { getLabels } from '../field.labels';


@Component({
	selector: 'common-address-field',
	standalone: true,
	imports: [FormsModule],
	templateUrl: './common-address-field.widget.html',
	host: { '[class.d-none]': 'isExcluded()' },
})
export class CommonAddressFieldWidget extends CommonFieldWidgetBase<AddressData | undefined, CommonAddressField> implements OnInit, AfterViewInit {

	private util = inject(UtilityService);
	private googleApi = inject(GoogleApiService);
	private readonly destroyRef = inject(DestroyRef);
	private places!: typeof google.maps.places;

	public readonly field = input.required<CommonAddressField>();
	public readonly addressField = viewChild<ElementRef<HTMLInputElement>>('addressField');
	suggestions = signal<google.maps.places.AutocompleteSuggestion[]>([]);
	highlighted = signal(0);

	// public foundPlace = signal<AddressData | undefined>(undefined);

	private districts = ['DC'];
	private territories = ['AS', 'GU', 'MP', 'PR', 'VI'];

	public readonly labels = computed(() => getLabels(this.field().config().isSpanish));


	public readonly renderedModel = computed(() => {
		const value = this.field().pendingValue() ?? this.field().actualValue();
		return this.render(value);
	});

	onInputChange(value: string) {
		// If the input is cleared, reset the pending value and return
		if (!value || !value.length) {
			this.field().pendingValue.set(undefined);
			return;
		}
		const pendingValue: AddressData = {
			placeAddress: value,
			inUS: false,
			noSelection: true,
			streetAddress: undefined,
			zipId: undefined,
			placeId: undefined,
			latitude: undefined,
			longitude: undefined,
			stateCode: undefined,
		}
		this.field().pendingValue.set(pendingValue)
	}

	public errorToDisplay = computed(() => {
		const error = this.error();
		if (error == '') return '';
		if (this.hasFocus() || !this.hadFocus()) return '';
		return error;
	});



	async ngOnInit() {

		this.onInit(this.field());

		this.places = await (await this.googleApi.getGoogleMapPlaces()).places;
	}

	ngAfterViewInit() {
		// listen to keystrokes with debounce
		const addressFieldElement = this.addressField()?.nativeElement;
		if (addressFieldElement) {
			fromEvent<InputEvent>(addressFieldElement, 'input').pipe(
				map(e => (e.target as HTMLInputElement).value.trim()),
				debounceTime(200),
				takeUntilDestroyed(this.destroyRef)
			).subscribe(query => this.getSuggestions(query));
		}
	}

	private async getSuggestions(text: string) {
		if (!text) { this.suggestions.set([]); return; }

		const { suggestions } = await this.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: text });
		this.suggestions.set(suggestions);
		this.highlighted.set(0);
	}

	onKeyDown(e: KeyboardEvent) {
		const suggestions = this.suggestions();
		if (!suggestions.length) { return; }

		const max = suggestions.length - 1;
		const currentHighlight = this.highlighted();

		switch (e.key) {
			case 'ArrowDown': {
				e.preventDefault();
				this.highlighted.set(Math.min(max, currentHighlight + 1));
				break;
			}
			case 'ArrowUp': {
				e.preventDefault();
				this.highlighted.set(Math.max(0, currentHighlight - 1));
				break;
			}
			case 'Enter': {
				e.preventDefault();
				const selectedSuggestion = suggestions[currentHighlight];
				if (selectedSuggestion) {
					this.onAddressSelection(selectedSuggestion);
				}
				break;
			}
		}
	}

	async onAddressSelection(selection: google.maps.places.AutocompleteSuggestion) {
		this.field().pendingValue.set(undefined);
		const place = await selection.placePrediction?.toPlace();
		if (place) {
			await place.fetchFields({ fields: ['id', 'addressComponents', 'formattedAddress', 'location', 'displayName', 'websiteURI'] });

			if (place.location) {
				const address = (place.formattedAddress ?? '').trim();
				const { street, zipId, inUS, stateCode, country, areaLevel1, areaLevel2 } = this.getAddressComponents(place.addressComponents);

				let county: string | undefined = undefined;
				if (stateCode) {
					county = this.getCounty(stateCode, country, areaLevel1, areaLevel2);
				}
				// Pre-fetch countyFipsId
				let countyFipsId: number | undefined = undefined;
				if (stateCode && county) { // both needed to get countyFipsId
					countyFipsId = await this.field().config().funcGetCountyFipsId({ stateCode: stateCode!, countyName: county! });
				}

				this.onChange({
					streetAddress: street,
					placeAddress: address,
					displayName: place.displayName ?? '',
					websiteURI: place.websiteURI ?? undefined,
					zipId,
					stateCode,
					county,
					countyFipsId,
					inUS: inUS ?? false,
					latitude: place.location ? Math.round(place.location.lat() * 100) : undefined,
					longitude: place.location ? Math.round(place.location.lng() * 100) : undefined,
					placeId: place.id
				});
				this.suggestions.set([]);
			}
		}
	}


	private getAddressComponents(parts: google.maps.places.AddressComponent[] | undefined) {
		const data: ModifiedAddressComponent = {
			zipId: undefined,
			inUS: undefined,
			country: undefined,
			street: undefined,
			stateCode: undefined,
			areaLevel1: undefined,
			areaLevel2: undefined,
			county: undefined
		};


		if (!parts) return data;

		let isTerritory = false;

		const countryPart = parts.find(part => (part.types ?? []).includes("country"));
		if (countryPart) {
			if (countryPart.shortText) {
				isTerritory = this.territories.includes(countryPart.shortText);
				data.inUS = countryPart.shortText == "US" || isTerritory;
				data.stateCode = isTerritory ? countryPart.shortText : undefined; // for territories, set country code as stateCode
			} else {
				data.inUS = false;
			}
			data.country = countryPart.longText ?? undefined;
		}

		if (data.inUS) {
			const zipPart = parts.find(part => (part.types ?? []).includes('postal_code'));
			if (zipPart && zipPart.shortText) data.zipId = parseInt(zipPart.shortText, 10);

			// administrative_area_level_1 is usually state for US States and county/city for territories
			const areaLevel1 = parts.find(part => (part.types ?? []).includes('administrative_area_level_1'));
			if (areaLevel1) {
				data.areaLevel1 = areaLevel1.longText ?? undefined;
				// for US states, set stateCode from administrative_area_level_1
				if (!isTerritory) {
					data.stateCode = areaLevel1.shortText ?? undefined;
				}
			}

			// administrative_area_level_2 is usually county for US States
			const areaLevel2 = parts.find(part => (part.types ?? []).includes('administrative_area_level_2'));
			if (areaLevel2) data.areaLevel2 = areaLevel2.shortText ?? undefined;

			const streetNumPart = parts.find(part => (part.types ?? []).includes("street_number"));
			const streetNamePart = parts.find(part => (part.types ?? []).includes("route"));
			const aptPart = parts.find(part => (part.types ?? []).includes("subpremise"));
			const street: string[] = [];
			if (streetNumPart && streetNumPart.longText) street.push(streetNumPart.longText);
			if (streetNamePart && streetNamePart.longText) street.push(streetNamePart.longText);
			if (aptPart && aptPart.longText) street.push(aptPart.longText);

			data.street = street.join(' ') || undefined;
		}
		return data;
	}

	private getCounty(stateCode: string, countryName: string | undefined, areaLevel1: string | undefined, areaLevel2: string | undefined): string | undefined {

		// If StateCode is 'GU'(Guam), return country name
		if (stateCode == 'GU') return countryName;

		// If StateCode is 'PR'(Puerto Rico), return level1 with 'Municipio' suffix
		if (stateCode == 'PR') return `${areaLevel1} Municipio`;

		// If StateCode is 'MP'(Northern Mariana Islands), return level1 with 'Municipality' suffix
		if (stateCode == 'MP') return `${areaLevel1} Municipality`;

		// If StateCode is in districts or territories('VI', 'AS'), return level1 as county
		if (this.districts.includes(stateCode) || this.territories.includes(stateCode)) return areaLevel1;

		return areaLevel2;
	}


	public onChange(value: AddressData | undefined) {
		this.processChange(value);
	}


	public async onBlur() {

		this.suggestions.set([]);

		await this.processBlur();
	}


	protected override render(value: AddressData | undefined) {
		return value?.placeAddress;
	}


	protected validate(placeFound: AddressData | undefined): string {

		const config = this.field().config();
		const labels = this.labels();
		const elementValue = placeFound?.placeAddress;

		// check if placeAddress exists and not a empty string
		if (config.isRequired && (!elementValue || !elementValue.length)) return labels.missingRequired();
		if (placeFound && placeFound.noSelection) return labels.selectValidAddress();
		if (elementValue && (!placeFound?.zipId || !placeFound?.streetAddress)) return labels.invalidMailingAddress();
		if (elementValue && (!placeFound?.countyFipsId && !placeFound?.stateCode)) return labels.invalidCountyFipsId();

		return '';
	}


	public setFocus() {
		console.error('setFocus() not implemented');
	}


	protected override valuesAreSame(value1: AddressData | undefined, value2: AddressData | undefined): boolean {
		return this.util.values.areSame(value1, value2);
	}
}